import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { DateObject } from 'react-multi-date-picker'
import { Compare } from '../api/types'
import { useGetAnalysisDevicesQuery, useGetAnalysisLocationsQuery, useGetParametersQuery } from '../api/analysisApi'
import RangeDatePicker from '../components/RangePicker'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { Button, Center, Checkbox, Divider, Flex, Stack } from '@chakra-ui/react'
import SingleSelect from '../../reports/components/SingleSelect'

const getSearchParams = ({
  dateRange1,
  dateRange2,
  deviceId1,
  deviceId2,
  parameterId,
  shouldCompareOneDevice,
}: any) => {
  const [startDate, endDate] = dateRange1.split('-')
  const date_ranges = [
    {
      from_date: dayjs(startDate).format('YYYY-MM-DD'),
      to_date: dayjs(endDate).format('YYYY-MM-DD'),
    },
  ]
  const devices = [deviceId1]
  if (shouldCompareOneDevice) {
    const [secondStartDate, secondEndDate] = dateRange2.split('-')
    date_ranges.push({
      from_date: dayjs(secondStartDate).format('YYYY-MM-DD'),
      to_date: dayjs(secondEndDate).format('YYYY-MM-DD'),
    })
  } else {
    devices.push(deviceId2)
  }

  return {
    date_ranges: date_ranges,
    devices: devices,
    parameterId,
    shouldCompareOneDevice,
  }
}

const CompareForm: React.FC = () => {
  const { data: { results: locations = [] } = {} } = useGetAnalysisLocationsQuery()
  const { register, watch, handleSubmit, setValue } = useForm<Compare.FormFields>()
  const location1 = watch('locationId1')
  const location2 = watch('locationId2')
  const parameter = watch('parameterId')
  const device1 = watch('deviceId1')
  const device2 = watch('deviceId2')

  const { data } = useGetParametersQuery(undefined)

  const { data: primaryDevices } = useGetAnalysisDevicesQuery(
    {
      locationId: location1,
      parameter_id: watch('parameterId'),
    },
    {
      skip: (!location1 || !watch('parameterId')),
    },
  )

  const { data: secondaryDevices } = useGetAnalysisDevicesQuery(
    {
      locationId: location2,
      parameter_id: watch('parameterId'),
    },
    {
      skip: (!location2 || !watch('parameterId')),
    },
  )

  const parameterSelect = useMemo(
    () =>
      data?.map(({ id, title }) => ({
        id,
        name: title,
      })),
    [data],
  )

  const primaryLocationSelect = useMemo(
    () =>
      locations?.map(({ id, title }) => ({
        id,
        name: title,
      })),
    [locations],
  )

  const secondaryLocationSelect = useMemo(
    () =>
      locations?.map(({ id, title }) => ({
        id,
        name: title,
      })),
    [locations],
  )

  const primaryDeviceSelect = useMemo(
    () =>
      primaryDevices?.map(({ id, title }) => ({
        id,
        name: title,
      })),
    [primaryDevices],
  )

  const secondaryDeviceSelect = useMemo(
    () =>
      secondaryDevices?.map(({ id, title }) => ({
        id,
        name: title,
      })),
    [secondaryDevices],
  )

  const setSelectedParameter = (selectedParameter: any) => {
    setValue('parameterId', selectedParameter)
  }
  const setSelectedLocation1 = (selectedId: any) => {
    setValue('locationId1', selectedId)
  }
  const setSelectedLocation2 = (selectedId: any) => {
    setValue('locationId2', selectedId)
  }
  const setSelectedDevice1 = (selectedId: any) => {
    setValue('deviceId1', selectedId)
  }
  const setSelectedDevice2 = (selectedId: any) => {
    setValue('deviceId2', selectedId)
  }

  const navigate = useNavigate()
  const onSubmit = ({
    dateRange1,
    dateRange2,
    deviceId1,
    deviceId2,
    parameterId,
    shouldCompareOneDevice,
  }: Compare.FormFields) => {

    const searchParams = getSearchParams({
      dateRange1,
      dateRange2,
      deviceId1,
      deviceId2,
      parameterId,
      shouldCompareOneDevice,
    })

    navigate('/analysis/compare-analysis', { state: searchParams })
  }

  return (
    <Flex gap={6} direction="column" as="form" onSubmit={handleSubmit(onSubmit)} >
      <Checkbox {...register('shouldCompareOneDevice')}>Compare one device</Checkbox>
      <Stack spacing={6}>
        <SingleSelect
          key="location1-id"
          items={primaryLocationSelect}
          isDisabled={false}
          {...register('locationId1')}
          setSelectedOption={setSelectedLocation1}
          selectedOption={location1}
          required={location1 === null}
          header="Select location"
        />
        <SingleSelect
          key="parameter-id"
          items={parameterSelect}
          isDisabled={false}
          {...register('parameterId')}
          setSelectedOption={setSelectedParameter}
          selectedOption={parameter}
          required={parameter === null}
          header="Select parameter"
        />
        {(location1 && watch('parameterId')) && (
          <SingleSelect
            key="device1-id"
            items={primaryDeviceSelect}
            isDisabled={false}
            {...register('deviceId1')}
            setSelectedOption={setSelectedDevice1}
            selectedOption={device1}
            required={device1 === null}
            header="Select device"
          />
        )}
      </Stack>
      <RangeDatePicker
        {...register('dateRange1')}
        onDatePickerChange={(from: DateObject, to: DateObject) => {
          setValue('dateRange1', `${from}-${to}`)
        }}
      />

      <Center>
        <Divider orientation="horizontal" w="full" />
      </Center>

      {watch('shouldCompareOneDevice') ? (
        <>
          <RangeDatePicker
            {...register('dateRange2')}
            onDatePickerChange={(from: DateObject, to: DateObject) => {
              setValue('dateRange2', `${from}-${to}`)
            }}
          />
        </>
      ) : (
        <>
          <SingleSelect
            key="location2-id"
            items={secondaryLocationSelect}
            isDisabled={false}
            {...register('locationId2')}
            setSelectedOption={setSelectedLocation2}
            selectedOption={location2}
            required={location2 === null}
            header="Select location"
          />
          {location2 && (
            <SingleSelect
              key="device2-id"
              items={secondaryDeviceSelect}
              isDisabled={false}
              {...register('deviceId2')}
              setSelectedOption={setSelectedDevice2}
              selectedOption={device2}
              required={device2 === null}
              header="Select device"
            />
          )}
        </>
      )}

      <Button
        mt={8}
        colorScheme="button.primary"
        type="submit"
        disabled={
          watch('shouldCompareOneDevice')
            ? !(watch('deviceId1') && watch('dateRange1') && watch('dateRange2'))
            : !(watch('deviceId1') && watch('deviceId2'))
        }
        w={240}
        h={12}
      >
        Compare
      </Button>
    </Flex >
  )
}

export default CompareForm
